import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, Heart } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PlaceSearch from "@/components/earthpulse/PlaceSearch";
import SubscribedBadge from "@/components/earthpulse/SubscribedBadge";
import { createCompatibilitySession, getSubscriberEmail } from "@/lib/api";
import useSubscription from "@/hooks/useSubscription";
import { SITE_URL } from "@/lib/site";
import { toast } from "sonner";

const inputCls =
  "bg-white border-slate-300 focus-visible:ring-[color:var(--gold)] focus-visible:border-[color:var(--gold)] rounded-lg h-11 text-slate-900 font-sans-body font-medium placeholder:text-slate-500 placeholder:font-normal";

const blank = () => ({
  name: "", birth_date: "", birth_time: "",
  latitude: "", longitude: "", place_label: "",
});

export default function CompatibilityForm() {
  const { active: subActive, email: subEmail } = useSubscription();
  const [a, setA] = useState(blank);
  const [b, setB] = useState(blank);
  const [email, setEmail] = useState(getSubscriberEmail() || "");
  const [skipEmail, setSkipEmail] = useState(false);
  const [relationship, setRelationship] = useState("partners");
  const [promoCode, setPromoCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (subEmail && !email) setEmail(subEmail);
  }, [subEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitLabel = subActive
    ? "Generate reading · included"
    : "Continue — subscribe to unlock (£8.88 / mo)";

  const setPerson = (which, k, v) => {
    (which === "a" ? setA : setB)((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!a.name || !b.name) { toast.error("Please enter both names"); return; }
    if (!a.birth_date || !b.birth_date) { toast.error("Both people need a birth date"); return; }
    if (!skipEmail && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      toast.error("Enter an email or tick 'show me on screen only'");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        person_a: {
          name: a.name,
          birth_date: a.birth_date,
          birth_time: a.birth_time || null,
          latitude: a.latitude ? parseFloat(a.latitude) : null,
          longitude: a.longitude ? parseFloat(a.longitude) : null,
          place_label: a.place_label || null,
        },
        person_b: {
          name: b.name,
          birth_date: b.birth_date,
          birth_time: b.birth_time || null,
          latitude: b.latitude ? parseFloat(b.latitude) : null,
          longitude: b.longitude ? parseFloat(b.longitude) : null,
          place_label: b.place_label || null,
        },
        email: skipEmail ? null : email,
        relationship,
        origin_url: SITE_URL,
        promo_code: promoCode ? promoCode.trim() : null,
      };
      const response = await createCompatibilitySession(payload);
      const { checkout_url, promo_applied, needs_subscription, subscribe_url } = response;
      if (needs_subscription) {
        toast("Add a £8.88/month membership to unlock unlimited readings.",
          { description: "You'll be redirected in a moment." });
        try { localStorage.setItem("ep_subscriber_email", (email || "").trim().toLowerCase()); } catch { /* noop */ }
        setTimeout(() => { window.location.href = subscribe_url; }, 800);
        return;
      }
      if (promo_applied) {
        toast.success("Promo code applied — enjoy your free reading");
      }
      window.location.href = checkout_url;
    } catch (err) {
      toast.error("Could not start checkout", { description: err?.response?.data?.detail || "" });
      setLoading(false);
    }
  };

  const noNative = { onInvalid: (ev) => ev.preventDefault() };

  return (
    <div className="min-h-screen relative z-10">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="back-home">
            <ArrowLeft size={16} className="text-[color:var(--gold)] group-hover:-translate-x-0.5 transition-transform" />
            <span className="label-overline">Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-[color:var(--rose-gold)]" />
            <span className="font-serif-display text-white text-lg">Cosmic Compatibility</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 md:px-10 py-12 md:py-16">
        {/* Intro block */}
        <div className="text-center max-w-3xl mx-auto stagger-in">
          <div className="label-overline flex items-center justify-center gap-3">
            A two-person reading <SubscribedBadge />
          </div>
          <h1 className="mt-5 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.02] tracking-tight">
            How your <span className="font-serif-italic text-[color:var(--gold)]">charts</span> meet.
          </h1>
          <p className="mt-6 text-[color:var(--parchment-soft)] text-lg font-sans-body leading-relaxed">
            Enter both birthdays. We&apos;ll draw the two natal wheels side by side, calculate the classical synastry between them, and our proprietary narrative engine will write you a warm, honest reading — how you meet, how you love, where you clash, and a small ritual to share.
          </p>
          <p className="mt-4 font-serif-italic text-[color:var(--text-secondary)] text-sm">
            {subActive
              ? "Included with your Earth Pulse membership · unlimited readings"
              : "£8.88 / month · unlimited readings · cancel anytime"}
          </p>
        </div>

        <form onSubmit={submit} noValidate className="mt-12 md:mt-16 space-y-8" data-testid="compat-form">
          {/* Relationship selector */}
          <div className="parchment-card rounded-2xl p-6 md:p-8 stagger-in">
            <div className="label-overline">You are</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {["partners", "considering it", "friends", "family", "coworkers", "just curious"].map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => setRelationship(r)}
                  className={`px-4 py-2 rounded-full text-sm transition ${
                    relationship === r
                      ? "bg-[color:var(--gold)] text-[color:var(--bg-base)] font-medium"
                      : "border border-[color:var(--gold)]/40 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10"
                  }`}
                  data-testid={`relationship-${r.replace(/\s/g, '-')}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Two person cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PersonCard which="a" label="Person A" state={a} setPerson={setPerson} testidPrefix="person-a" />
            <PersonCard which="b" label="Person B" state={b} setPerson={setPerson} testidPrefix="person-b" />
          </div>

          {/* Email + submit */}
          <div className="parchment-card rounded-2xl p-8 md:p-10 stagger-in">
            <div className="label-overline">Where shall we send your reading?</div>
            <div className="mt-4">
              <Input
                className={inputCls}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={skipEmail ? "Not required — reading shown on screen" : "you@example.com"}
                data-testid="compat-email"
                disabled={skipEmail}
                {...noNative}
              />
              <label className="mt-3 flex items-center gap-2 text-[color:var(--text-secondary)] text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={skipEmail}
                  onChange={(e) => setSkipEmail(e.target.checked)}
                  className="w-4 h-4 accent-[color:var(--gold)]"
                  data-testid="compat-skip-email"
                />
                <span className="font-serif-italic">Skip email · show me on screen only</span>
              </label>
            </div>
            <div className="mt-6 pt-6 border-t border-[color:var(--gold)]/15">
              <Label className="label-overline text-[color:var(--text-secondary)]">Promo code</Label>
              <Input
                className={inputCls}
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Optional — e.g. ABC123"
                data-testid="compat-promo-code"
                autoCapitalize="characters"
                autoComplete="off"
              />
              <p className="mt-2 font-serif-italic text-[color:var(--text-muted)] text-xs">
                A valid promo code skips payment — you go straight to your reading.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-[color:var(--gold)]/15">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-13 py-4 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-sans-body tracking-wide text-base"
                data-testid="compat-submit"
              >
                {loading ? "Preparing your reading…" : (promoCode ? "Continue with promo code" : submitLabel)}
              </Button>
              <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs text-center">
                {subActive
                  ? "Included with your Earth Pulse membership · unlimited readings."
                  : "Membership managed by Stripe. Cancel any time from the billing portal."}
              </p>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

function PersonCard({ label, state, setPerson, which, testidPrefix }) {
  const noNative = { onInvalid: (ev) => ev.preventDefault() };
  return (
    <div className="parchment-card rounded-2xl p-6 md:p-8 stagger-in" data-testid={`${testidPrefix}-card`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center">
          <Sparkles size={13} className="text-[color:var(--gold)]" />
        </div>
        <div className="label-overline">{label}</div>
      </div>
      <div className="space-y-4">
        <div>
          <Label className="label-overline text-[color:var(--text-secondary)]">Their name *</Label>
          <Input
            className={inputCls}
            value={state.name}
            onChange={(e) => setPerson(which, "name", e.target.value)}
            placeholder="First name is fine"
            data-testid={`${testidPrefix}-name`}
            {...noNative}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="label-overline text-[color:var(--text-secondary)]">Birth date *</Label>
            <Input
              className={inputCls}
              type="date"
              value={state.birth_date}
              onChange={(e) => setPerson(which, "birth_date", e.target.value)}
              data-testid={`${testidPrefix}-date`}
              {...noNative}
            />
          </div>
          <div>
            <Label className="label-overline text-[color:var(--text-secondary)]">Birth time</Label>
            <Input
              className={inputCls}
              type="time"
              value={state.birth_time}
              onChange={(e) => setPerson(which, "birth_time", e.target.value)}
              data-testid={`${testidPrefix}-time`}
            />
          </div>
        </div>
        <div>
          <Label className="label-overline text-[color:var(--text-secondary)]">Place of birth</Label>
          <PlaceSearch
            value={state.place_label}
            onChange={(v) => setPerson(which, "place_label", v)}
            onSelect={(r) => {
              setPerson(which, "place_label", r.label);
              setPerson(which, "latitude", r.lat.toFixed(4));
              setPerson(which, "longitude", r.lng.toFixed(4));
            }}
            testid={`${testidPrefix}-place`}
          />
        </div>
        <p className="font-serif-italic text-[color:var(--text-muted)] text-xs">
          Time & place unlock Moon and Rising. Date alone is plenty to start.
        </p>
      </div>
    </div>
  );
}
